import React, { FC, ChangeEventHandler, useMemo, useCallback } from 'react';
import classnames from 'classnames';
import { useIntl } from 'react-intl';
import dayjs from 'dayjs';

import {
  ReservationFlowPaymentMethodListSubscription,
  ReservationFlowSelectedPaymentMethod,
  ReservationFlowSelectedPaymentMethodNewCreditCard,
  ReservationFlowSelectedPaymentMethodSubscription,
  ReservationFlowCustomerSubscriptionAvailableReservationCount,
} from 'src/interfaces/reservation_flow';
import { RSBorderedListItemCollapsible } from 'src/reservation-page/bordered-list/RSBorderedListItemCollapsible';
import { RSCheckRadio } from 'src/reservation-page/RSCheckRadio';

import { useDisplayBehavior } from './hooks/useDisplayBehavior';

export interface SubscriptionItemProps {
  value?:
    | ReservationFlowSelectedPaymentMethod
    | ReservationFlowSelectedPaymentMethodNewCreditCard;
  onChange: (
    paymentMethod: ReservationFlowSelectedPaymentMethodSubscription,
  ) => void;
  onBlur: () => void;
  paymentMethodsSubscription: ReservationFlowPaymentMethodListSubscription;
  merchantTimeZone: string;
  customerSubscriptionAvailableReservationCounts?: ReservationFlowCustomerSubscriptionAvailableReservationCount[];
  isCollapsed: boolean;
  onRequireToggling: () => void;
  selectedDate?: string; // 現時点では複数日程に対応しない
}

export const SubscriptionItem: FC<SubscriptionItemProps> = ({
  value,
  onChange,
  onBlur,
  paymentMethodsSubscription,
  merchantTimeZone,
  customerSubscriptionAvailableReservationCounts,
  isCollapsed,
  onRequireToggling,
  selectedDate,
}) => {
  const intl = useIntl();

  const isSelected = useMemo(() => {
    return typeof value !== 'undefined' ? value.type === 'subscription' : false;
  }, [value]);

  const onInputChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
    (event) => {
      if (!event.target.checked) return;

      onChange({
        type: 'subscription',
        customer_subscription_public_id: parseInt(event.target.value, 10),
      });
      onBlur();
    },
    [onChange, onBlur],
  );

  const { items } = useDisplayBehavior({
    value,
    merchantTimeZone,
    paymentMethodsSubscription,
    customerSubscriptionAvailableReservationCounts,
    selectedDate,
  });

  return (
    <RSBorderedListItemCollapsible
      leftContent={
        <div className="rs-font text-16 text-gray-1000 pc:text-15 font-bold">
          {intl.formatMessage({
            defaultMessage: '月謝',
            id: 'zfYOv4',
            description:
              'src/templates/merchants/resources/book/components/payment-method/SubscriptionItem/index.tsx',
          })}
        </div>
      }
      details={
        <div className="px-2.5 py-3">
          <ul className="list-style-none m-0 space-y-2.5 p-0">
            {items.map(({ product, isSelected, isDisabled, notes }) => {
              const active_customer_subscription =
                product.active_customer_subscriptions[0];
              return (
                <li
                  key={`paymentMethod_subscription_products_${product.public_id}`}
                  className="block"
                >
                  <label className="flex cursor-pointer items-center justify-between">
                    <div>
                      <div className="rs-font text-16 text-gray-1000 pc:text-15 flex-auto font-normal">
                        {product.name}
                      </div>
                      {active_customer_subscription.expires_at && (
                        <div className="rs-font text-13 text-gray-1000 pc:text-12 mt-0.5 flex-auto font-normal">
                          {intl.formatMessage(
                            {
                              defaultMessage: '有効期限：{expiresAt}',
                              id: '6cMcEV',
                              description:
                                'src/templates/merchants/resources/book/components/payment-method/SubscriptionItem/index.tsx',
                            },
                            {
                              expiresAt: dayjs(
                                active_customer_subscription.expires_at,
                              )
                                .tz('Asia/Tokyo')
                                .format('YYYY年M月D日'),
                            },
                          )}
                        </div>
                      )}
                    </div>
                    <div className="ml-1.5 flex-none">
                      <RSCheckRadio
                        name="paymentMethod"
                        value={active_customer_subscription.public_id.toString()}
                        checked={isSelected}
                        disabled={isDisabled}
                        onChange={onInputChange}
                      />
                    </div>
                  </label>

                  {(() => {
                    if (typeof notes === 'undefined' || notes.length === 0) {
                      return null;
                    }

                    return (
                      <div className="mt-0.5">
                        {notes.map(({ text, isWarning, details }, index) => {
                          return (
                            <div
                              key={`paymentMethod_subscription_products_${product.public_id}_text_${index}`}
                              className={classnames(
                                'rs-font',
                                'text-13',
                                'pc:text-12',
                                'font-normal',
                                !isWarning ? 'text-gray-1000' : 'text-red-700',
                              )}
                            >
                              <div>{text}</div>
                              {details && (
                                <div className="text-13 pc:text-12 ml-1 text-gray-400">
                                  <ol className="list-none border-0 border-l-[1px] border-solid border-gray-200 pl-1">
                                    {details.map((detail, j) => (
                                      <li
                                        key={`paymentMethod_subscription_products_${product.public_id}_text_${index}_details_${j}`}
                                      >
                                        {detail}
                                      </li>
                                    ))}
                                  </ol>
                                  <small className="text-12">
                                    {intl.formatMessage({
                                      defaultMessage: '※上から順に消費されます',
                                      id: 'm6gB+h',
                                      description:
                                        'src/templates/merchants/resources/book/components/payment-method/SubscriptionItem/index.tsx',
                                    })}
                                  </small>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </li>
              );
            })}
          </ul>
        </div>
      }
      isCollapsed={isCollapsed}
      isSelected={isSelected}
      onClick={onRequireToggling}
    />
  );
};
